import React from "react";
import { Box, MenuItem, TextField } from "@mui/material";
import { useIntl } from "react-intl";
import { ImpactLinkPicker } from "../Project/ImpactLink";
import LinkSettingsLayout from "../Project/LinkSettingsLayout";
import useProjects from "../../hooks/useProjects";
import { VOICE_OPTIONS, effectiveVoice } from "../../constants/voices";

// The classic survey builder's "Link a project" + language, beside the
// title.
//
// The impact assessment (Survey.project) is OPTIONAL: linked, the survey shows
// on its Evidence tab and its single-choice questions can be linked to its
// segments; it locks once a response has been completed (see
// components/Project/projectLink.js). The language (Survey.language) is what
// the report comes through in. The respondent experience (Survey.respond_mode)
// is the classic form or a voice interview over the same questions, with the
// voice it reads them in (Survey.voice).
export const SURVEY_LANGUAGES = [
    { value: "en", label: "English" },
    { value: "es", label: "Español" },
    { value: "fr", label: "Français" },
];

export function SurveyProjectFields({ settings, onChange }) {
    const intl = useIntl();
    const { projects, loading } = useProjects();
    const locked = Boolean(settings.projectLocked);
    const projectPicker = (
        <ImpactLinkPicker
            id="survey-project"
            sx={{ bgcolor: "white" }}
            projects={projects}
            loading={loading}
            value={settings.project || ""}
            locked={locked}
            compact
            onChange={(id) => onChange("project", id)}
        />
    );
    const languageSelect = (
        <TextField
            select
            fullWidth
            size="small"
            sx={{ bgcolor: "white" }}
            label={intl.formatMessage({ id: "survey.language_label", defaultMessage: "Assessment language" })}
            value={settings.language || "en"}
            onChange={(e) => onChange("language", e.target.value)}
            inputProps={{ "data-testid": "survey-language" }}
        >
            {SURVEY_LANGUAGES.map((l) => (
                <MenuItem key={l.value} value={l.value}>
                    {l.label}
                </MenuItem>
            ))}
        </TextField>
    );
    const isVoice = settings.respondMode === "voice";
    const delivery = (
        <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
                select
                fullWidth
                size="small"
                sx={{ bgcolor: "white" }}
                label={intl.formatMessage({ id: "survey.respond_mode_label", defaultMessage: "Respondent experience" })}
                value={settings.respondMode || "traditional"}
                onChange={(e) => onChange("respondMode", e.target.value)}
                inputProps={{ "data-testid": "survey-respond-mode" }}
            >
                <MenuItem value="traditional">
                    {intl.formatMessage({ id: "survey.respond_mode_traditional", defaultMessage: "Traditional" })}
                </MenuItem>
                <MenuItem value="voice">
                    {intl.formatMessage({ id: "survey.respond_mode_voice", defaultMessage: "Modern" })}
                </MenuItem>
            </TextField>
            {isVoice && (
                <TextField
                    select
                    fullWidth
                    size="small"
                    sx={{ bgcolor: "white" }}
                    label={intl.formatMessage({ id: "survey.voice_label", defaultMessage: "Voice" })}
                    value={effectiveVoice(settings.voice)}
                    onChange={(e) => onChange("voice", e.target.value)}
                    inputProps={{ "data-testid": "survey-voice" }}
                >
                    {VOICE_OPTIONS.map((v) => (
                        <MenuItem key={v.value} value={v.value}>
                            {intl.formatMessage({ id: v.labelId, defaultMessage: v.defaultLabel })}
                        </MenuItem>
                    ))}
                </TextField>
            )}
        </Box>
    );
    return (
        <Box data-testid="survey-project-fields">
            <LinkSettingsLayout
                id="survey-link"
                project={projectPicker}
                language={languageSelect}
                delivery={delivery}
                compact
                sx={{ bgcolor: "white" }}
            />
        </Box>
    );
}
